import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  AuthRequiredModal,
  type AuthRequiredBenefit,
} from "@/components/auth/AuthRequiredModal";

/**
 * Which screen the gate opens on.
 *
 * `gate` is for a *blocked action*: the visitor did not ask to sign in, so the
 * gate has to earn it by explaining what an account is for. `signin` / `signup`
 * are for someone who already chose that path — showing them the reason screen
 * first would be a detour in front of a decision they have made.
 */
export type AuthGateStage = "gate" | "signin" | "signup";

/**
 * What the learner tried to do, in their own words.
 *
 * Every field is optional because the modal's defaults are already a complete,
 * honest answer. Pass a `title`/`description` only when the gate can say
 * something more specific than "sign in to continue" — "Sign in to keep your
 * recording" beats it every time.
 */
export interface RequireAuthOptions {
  title?: string;
  description?: string;
  benefits?: AuthRequiredBenefit[];
  /** Defaults to `gate`. */
  stage?: AuthGateStage;
}

interface AuthRequiredContextValue {
  /**
   * Open the gate and resolve `true` once the learner has signed in, or
   * `false` if they dismissed it. Awaiting it is how a caller resumes the
   * action they were blocked on:
   *
   * ```ts
   * if (!isAuthenticated && !(await requireAuth({ title: "Sign in to save" }))) return;
   * await save();
   * ```
   */
  requireAuth: (options?: RequireAuthOptions) => Promise<boolean>;
  /** Dismiss the gate, resolving any pending `requireAuth` with `false`. */
  cancelAuth: () => void;
}

const AuthRequiredContext = createContext<AuthRequiredContextValue | null>(null);

export const useAuthRequired = (): AuthRequiredContextValue => {
  const context = useContext(AuthRequiredContext);
  if (!context) {
    throw new Error("useAuthRequired must be used within an AuthRequiredProvider");
  }
  return context;
};

interface PendingRequest {
  options: RequireAuthOptions;
  /** Remount key — a new request gets a fresh gate rather than the last view. */
  id: number;
}

/**
 * The single sign-in gate for the whole app.
 *
 * Mounted once, above the routes, so every gated action shares one modal
 * instead of each component owning its own. Two benefits fall out of that:
 * there is exactly one dialog to style and translate, and the gate cannot be
 * open twice (three components used to mount their own, so two blocked actions
 * could stack two modals).
 *
 * See `RequireAuth` for the *route* guard, which is a different job: it renders
 * a page's content behind the auth surface rather than gating a single action.
 */
export const AuthRequiredProvider = ({ children }: { children: ReactNode }) => {
  const [pending, setPending] = useState<PendingRequest | null>(null);
  const resolverRef = useRef<((authed: boolean) => void) | null>(null);
  const idRef = useRef(0);

  const settle = useCallback((authed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    resolve?.(authed);
  }, []);

  const requireAuth = useCallback((options: RequireAuthOptions = {}) => {
    idRef.current += 1;
    const id = idRef.current;

    return new Promise<boolean>((resolve) => {
      // A second request while the gate is already open replaces the first —
      // it does not stack. The earlier caller is resolved `false` so nothing
      // sits pending forever waiting on a gate that was overwritten.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setPending({ options, id });
    });
  }, []);

  const cancelAuth = useCallback(() => settle(false), [settle]);

  const value = useMemo(
    () => ({ requireAuth, cancelAuth }),
    [requireAuth, cancelAuth],
  );

  return (
    <AuthRequiredContext.Provider value={value}>
      {children}
      <AuthRequiredModal
        key={pending?.id ?? "closed"}
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
        title={pending?.options.title}
        description={pending?.options.description}
        benefits={pending?.options.benefits}
        initialStage={pending?.options.stage}
        onAuthed={() => settle(true)}
      />
    </AuthRequiredContext.Provider>
  );
};
